import type { IExperimentSession, ITuningClock, TuningExperimentRequest } from '@playcastdotio/PeerCore';
import type { HostStreamTuningBinding } from '../trials/HostStreamTuningBinding';
import { HostExperimentManager } from './HostExperimentManager';

/** Composes the runner over the same coordinator authority and existing fresh transaction. */
export function createHostExperimentSession(binding: HostStreamTuningBinding, clock: ITuningClock): IExperimentSession {
  const control = binding.control; const runs = new Map<string, { deadlineMs: number; leaseId: string }>();
  const leaseForRequest = (request: TuningExperimentRequest) => control.hasLease(request.sessionId, request.leaseId);
  return {
    authorize: request => leaseForRequest(request) && control.session(request.sessionId)?.target.configRevision === request.expectedRevision,
    production: sessionId => binding.productionConfiguration(sessionId),
    snapshot: sessionId => { const snapshot = control.session(sessionId); if (!snapshot) throw new Error('unknown-session'); return snapshot; },
    capabilities: sessionId => control.capabilities(sessionId).endpoints,
    acquire: (sessionId, ownerId, deadlineMs) => {
      const allowed = control.acquire(sessionId, ownerId, deadlineMs);
      if (allowed) runs.set(sessionId, { deadlineMs, leaseId: '' }); return allowed;
    },
    release: (sessionId, ownerId) => { control.release(sessionId, ownerId); runs.delete(sessionId); },
    execute: async (command, ownerId) => {
      const run = runs.get(command.target.sessionId); if (run) run.leaseId = command.leaseId;
      const receipt = await control.executeOwned(command, ownerId);
      if (receipt.stage !== 'applied') {
        const failed = control.session(command.target.sessionId);
        if (failed?.restoration) return failed;
        throw new Error(receipt.error?.message ?? 'experiment-command-failed');
      }
      const snapshot = control.session(command.target.sessionId); if (!snapshot) throw new Error('unknown-session'); return snapshot;
    },
    restoreAccepted: async (sessionId, ownerId, baseline) => {
      const run = runs.get(sessionId);
      if (!run || run.deadlineMs <= clock.monotonicMs() || !control.hasLease(sessionId, run.leaseId)) {
        await control.revoke(sessionId, 'experiment-expired');
        if (control.session(sessionId)?.restoration?.status === 'failed') throw new Error('baseline-restoration-failed');
        return;
      }
      const current = control.session(sessionId);
      if (current?.configurationHash !== baseline.configurationHash) await binding.restoreExperimentBaseline(sessionId, ownerId, baseline);
      const restored = control.session(sessionId);
      if (restored?.restoration?.status === 'failed' || restored?.phase === 'failed') throw new Error('baseline-restoration-failed');
    },
    stop: (sessionId, reason) => control.revoke(sessionId, reason),
    renewQualification: (request, deadlineMs) => control.renewQualification(request, deadlineMs),
    subscribe: listener => control.subscribe(listener), publish: snapshot => control.recordExperiment(snapshot),
  };
}
export function installHostExperiments(binding: HostStreamTuningBinding, clock: ITuningClock, id: () => string): HostExperimentManager {
  const manager = new HostExperimentManager({ clock, id, session: createHostExperimentSession(binding, clock), wait: waitForExperiment });
  manager.register(binding.control); return manager;
}
function waitForExperiment(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new Error('cancelled')); return; }
    const abort = () => { clearTimeout(timer); reject(new Error('cancelled')); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, delayMs);
    signal.addEventListener('abort', abort, { once: true });
  });
}
